import type { EaAnswers, EaValues } from "./types";
import { EA_GEFAHREN, gefahrenOhneKbu } from "./gefahren";

/**
 * Prüfregeln des Blattes „Soll-Ist-Abgleich“ der Felddefinition.
 *
 * Jede Regel vergleicht einen Sollwert aus der Risikoanalyse mit dem im
 * Vertrag erfassten Istwert und liefert eines der Ergebnisse der Spalte E:
 * „passend / prüfen / Deckungslücke“. Fehlt der Sollwert, bleibt die Regel
 * `offen` — sie wird angezeigt, aber nicht als Lücke gewertet.
 */

export type PruefStatus = "passend" | "pruefen" | "luecke" | "offen";

export type PruefErgebnis = {
  key: string;
  /** Spalte A — Prüfung. */
  pruefung: string;
  /** Spalte B — Soll aus Risikoanalyse. */
  sollLabel: string;
  /** Spalte C — Ist aus Vertragsmaske. */
  istLabel: string;
  /** Spalte D — Prüflogik. */
  logik: string;
  /** Spalte F — Hinweis. */
  hinweis: string;
  status: PruefStatus;
  /** Konkrete Begründung mit den erfassten Werten. */
  detail: string;
};

/** Deutsche wie englische Zahleneingaben in eine Zahl überführen. */
export const num = (raw: string | undefined): number | null => {
  const s = (raw ?? "").trim();
  if (!s) return null;
  // „1.234,56“ → „1234.56“; „1,234.56“ bleibt „1234.56“
  const normalized =
    s.includes(",") && s.lastIndexOf(",") > s.lastIndexOf(".")
      ? s.replace(/\./g, "").replace(",", ".")
      : s.replace(/,/g, "");
  const n = Number(normalized.replace(/[^0-9.\-]/g, ""));
  return Number.isFinite(n) ? n : null;
};

const eur = (n: number | null): string =>
  n === null ? "—" : `${n.toLocaleString("de-DE", { maximumFractionDigits: 0 })} €`;

const isJa = (v: EaValues, key: string) => v[key] === "ja";
const isNein = (v: EaValues, key: string) => v[key] === "nein";

/** Höchster im Vertrag wirksamer Betrag für den Rohertragsvergleich. */
const effektiveDeckung = (v: EaValues): number | null => {
  const kandidaten = [
    num(v["jahresrohertrag"]),
    num(v["vs_kbu"]),
  ].filter((n): n is number => n !== null);
  if (!kandidaten.length) return null;
  const basis = Math.max(...kandidaten);
  // Eine Höchstentschädigung begrenzt die tatsächliche Leistung.
  const limit = num(v["hoechstentschaedigung.limit_eur"]);
  const jhe = num(v["jahreshoechstentschaedigung.limit_eur"]);
  const grenzen = [limit, jhe].filter((n): n is number => n !== null && n > 0);
  return grenzen.length ? Math.min(basis, ...grenzen) : basis;
};

/** Vergleich Soll-Betrag ↔ Ist-Betrag mit 5 % Toleranz für „prüfen“. */
const betragStatus = (soll: number | null, ist: number | null): PruefStatus => {
  if (soll === null) return "offen";
  if (ist === null) return "luecke";
  if (ist >= soll) return "passend";
  return ist >= soll * 0.95 ? "pruefen" : "luecke";
};

/** Deckungsbaustein: Bedarf vorhanden, Vertrag fehlt → Lücke. */
const deckungStatus = (
  sollJa: boolean,
  sollOffen: boolean,
  istJa: boolean,
  sollLimit: number | null,
  istLimit: number | null,
  sollHaftzeit: number | null,
  istHaftzeit: number | null,
): PruefStatus => {
  if (sollOffen) return "offen";
  if (!sollJa) return "passend";
  if (!istJa) return "luecke";
  if (sollLimit !== null && istLimit !== null && istLimit < sollLimit) return "luecke";
  if (sollLimit !== null && istLimit === null) return "pruefen";
  if (sollHaftzeit !== null && istHaftzeit !== null && istHaftzeit < sollHaftzeit) return "luecke";
  if (sollHaftzeit !== null && istHaftzeit === null) return "pruefen";
  return "passend";
};

/** Alter der Summenermittlung in Jahren, ab dem eine Neuprüfung ausgelöst wird. */
export const SUMMENAKTUALITAET_JAHRE = 3;

export const pruefeAbgleich = (answers: EaAnswers, heute: Date): PruefErgebnis[] => {
  const v = answers.values;
  const ergebnisse: PruefErgebnis[] = [];

  // Zeile 4 der Vertragsmaske: keine Deckung, aber Bedarf → Deckungslücke.
  const irgendeinBedarf =
    num(v["soll_rohertrag"]) !== null ||
    num(v["soll_haftzeit"]) !== null ||
    isJa(v, "soll_rueckwirkung") ||
    isJa(v, "soll_wechselwirkung") ||
    isJa(v, "soll_tbu") ||
    isJa(v, "soll_betriebsschliessung");
  ergebnisse.push({
    key: "deckung",
    pruefung: "Deckung dem Grunde nach",
    sollLabel: "Soll-Bedarf aus Risikoanalyse",
    istLabel: "Betriebsunterbrechungs-/Ertragsausfalldeckung vorhanden",
    logik: "Wenn keine Deckung besteht, der Bedarf aber vorhanden ist → Deckungslücke.",
    hinweis: "Ohne Deckung sind alle folgenden Prüfungen gegenstandslos.",
    status: isNein(v, "deckung_vorhanden")
      ? irgendeinBedarf
        ? "luecke"
        : "pruefen"
      : v["deckung_vorhanden"]
        ? "passend"
        : "offen",
    detail: isNein(v, "deckung_vorhanden")
      ? irgendeinBedarf
        ? "Keine Deckung erfasst, die Risikoanalyse weist aber Bedarf aus."
        : "Keine Deckung erfasst — Bedarf ist noch nicht bewertet."
      : v["deckung_vorhanden"]
        ? "Deckung ist vorhanden."
        : "Noch nicht beantwortet.",
  });

  // 1 · Versicherungsrohertrag / VS
  const sollRohertrag = num(v["soll_rohertrag"]);
  const istDeckung = effektiveDeckung(v);
  ergebnisse.push({
    key: "rohertrag",
    pruefung: "Versicherungsrohertrag / VS",
    sollLabel: "Soll Versicherungsrohertrag",
    istLabel: "Versicherter Jahresrohertrag / VS / Höchstentschädigung",
    logik: "Istwert und effektive Höchstentschädigung gegen Sollwert prüfen.",
    hinweis:
      "Bemessungsgrundlage beachten; keine rein numerische Prüfung, wenn Vertragslogik abweicht.",
    status: betragStatus(sollRohertrag, istDeckung),
    detail: `Soll ${eur(sollRohertrag)} · effektiv gedeckt ${eur(istDeckung)}${
      v["bemessungsgrundlage"] ? ` · Basis: ${v["bemessungsgrundlage"]}` : ""
    }`,
  });

  // 2 · Haftzeit
  const sollHaftzeit = num(v["soll_haftzeit"]);
  const istHaftzeit = num(v["haftzeit"]);
  ergebnisse.push({
    key: "haftzeit",
    pruefung: "Haftzeit",
    sollLabel: "Soll Haftzeit",
    istLabel: "Haftzeit",
    logik: "Vertragliche Haftzeit < Sollhaftzeit → Deckungslücke / Prüfung.",
    hinweis: "Abweichende Haftzeiten einzelner Teildeckungen separat prüfen.",
    status:
      sollHaftzeit === null
        ? "offen"
        : istHaftzeit === null
          ? "luecke"
          : istHaftzeit >= sollHaftzeit
            ? "passend"
            : "luecke",
    detail: `Soll ${sollHaftzeit ?? "—"} Monate · Vertrag ${istHaftzeit ?? "—"} Monate${
      isJa(v, "haftzeit_abweichend") ? " · abweichende Teildeckungs-Haftzeit erfasst" : ""
    }`,
  });

  // 3 · Mehrkosten (Mehrkosten + zusätzliche Mehrkosten)
  const sollMehrkosten = num(v["soll_mehrkosten"]);
  const istMehrkosten = [
    isJa(v, "mehrkosten") ? num(v["mehrkosten.limit_eur"]) : null,
    isJa(v, "zusaetzliche_mehrkosten") ? num(v["zusaetzliche_mehrkosten.limit_eur"]) : null,
  ].reduce<number | null>((acc, n) => (n === null ? acc : (acc ?? 0) + n), null);
  ergebnisse.push({
    key: "mehrkosten",
    pruefung: "Mehrkosten",
    sollLabel: "Soll Mehrkostenlimit",
    istLabel: "Mehrkosten + zusätzliche Mehrkosten",
    logik: "Fehlende Deckung oder Limit unter Soll → Prüfung / Deckungslücke.",
    hinweis:
      "Mehrkosten können Ertragsausfall reduzieren; Limit muss zum Notfallkonzept passen.",
    status:
      sollMehrkosten === null
        ? "offen"
        : !isJa(v, "mehrkosten") && !isJa(v, "zusaetzliche_mehrkosten")
          ? "luecke"
          : betragStatus(sollMehrkosten, istMehrkosten),
    detail: `Soll ${eur(sollMehrkosten)} · Vertrag ${eur(istMehrkosten)}`,
  });

  // 4 · Mietausfall
  const sollMietEur = num(v["soll_mietausfall.eur"]);
  const sollMietMonate = num(v["soll_mietausfall.monate"]);
  const istMietEur = isJa(v, "mietausfall")
    ? (num(v["mietausfall.vs"]) ?? num(v["mietausfall.limit_eur"]))
    : null;
  const istMietMonate = isJa(v, "mietausfall") ? num(v["mietausfall.haftzeit"]) : null;
  ergebnisse.push({
    key: "mietausfall",
    pruefung: "Mietausfall",
    sollLabel: "Soll Mietausfall",
    istLabel: "Mietausfall / Mietverlust",
    logik:
      "Bedarf vorhanden, aber Deckung fehlt oder Limit/Haftzeit zu klein → Deckungslücke.",
    hinweis: "Nur bei Vermietungs-/Untervermietungsrisiko.",
    status:
      sollMietEur === null && sollMietMonate === null
        ? "offen"
        : !isJa(v, "mietausfall")
          ? "luecke"
          : sollMietMonate !== null && istMietMonate !== null && istMietMonate < sollMietMonate
            ? "luecke"
            : betragStatus(sollMietEur, istMietEur),
    detail: `Soll ${eur(sollMietEur)}${sollMietMonate !== null ? ` / ${sollMietMonate} Monate` : ""} · Vertrag ${eur(istMietEur)}${
      istMietMonate !== null ? ` / ${istMietMonate} Monate` : ""
    }`,
  });

  // 5 + 6 · Rückwirkung Zulieferer / Abnehmer
  const sollRwOffen = !v["soll_rueckwirkung"];
  const sollRwJa = isJa(v, "soll_rueckwirkung");
  const sollRwLimit = num(v["soll_rueckwirkung.limit_eur"]);
  const sollRwHaft = num(v["soll_rueckwirkung.haftzeit"]);
  for (const [key, feld, label, hinweis] of [
    [
      "rueckwirkung_zulieferer",
      "rueckwirkung_zulieferer",
      "Rückwirkung Zulieferer",
      "Benannte vs. unbenannte Zulieferer / Sublimits berücksichtigen.",
    ],
    [
      "rueckwirkung_abnehmer",
      "rueckwirkung_abnehmer",
      "Rückwirkung Abnehmer",
      "Abnehmerkonzentration berücksichtigen.",
    ],
  ] as const) {
    const istJa = isJa(v, feld);
    ergebnisse.push({
      key,
      pruefung: label,
      sollLabel: `Soll ${label}`,
      istLabel: label,
      logik: "Bedarf Ja und Vertrag Nein → Deckungslücke; Limit und Haftzeit prüfen.",
      hinweis,
      status: deckungStatus(
        sollRwJa,
        sollRwOffen,
        istJa,
        sollRwLimit,
        istJa ? num(v[`${feld}.limit_eur`]) : null,
        sollRwHaft,
        istJa ? num(v[`${feld}.haftzeit`]) : null,
      ),
      detail: `Soll ${sollRwJa ? "Ja" : sollRwOffen ? "—" : "Nein"}${
        sollRwLimit !== null ? ` (${eur(sollRwLimit)})` : ""
      } · Vertrag ${istJa ? "Ja" : "Nein"}${
        istJa && v[`${feld}.limit_eur`] ? ` (${eur(num(v[`${feld}.limit_eur`]))})` : ""
      }`,
    });
  }

  // 7 · Wechselwirkung
  const istWwJa = isJa(v, "wechselwirkung_betriebsstaetten") || isJa(v, "abhaengigkeit_verbund");
  ergebnisse.push({
    key: "wechselwirkung",
    pruefung: "Wechselwirkung",
    sollLabel: "Soll Wechselwirkung",
    istLabel: "Wechselwirkung / Verbunddeckung",
    logik: "Kritische Standort-/Verbundabhängigkeit gegen Vertragsdeckung prüfen.",
    hinweis: "Versicherte Unternehmen/Standorte müssen korrekt zugeordnet sein.",
    status: deckungStatus(
      isJa(v, "soll_wechselwirkung"),
      !v["soll_wechselwirkung"],
      istWwJa,
      num(v["soll_wechselwirkung.limit_eur"]),
      istWwJa
        ? (num(v["wechselwirkung_betriebsstaetten.limit_eur"]) ??
          num(v["abhaengigkeit_verbund.limit_eur"]))
        : null,
      num(v["soll_wechselwirkung.haftzeit"]),
      istWwJa
        ? (num(v["wechselwirkung_betriebsstaetten.haftzeit"]) ??
          num(v["abhaengigkeit_verbund.haftzeit"]))
        : null,
    ),
    detail: `Soll ${isJa(v, "soll_wechselwirkung") ? "Ja" : v["soll_wechselwirkung"] ? "Nein" : "—"} · Vertrag ${
      istWwJa ? "Ja" : "Nein"
    }${answers.betriebsstaetten.length > 1 ? ` · ${answers.betriebsstaetten.length} Betriebsstätten versichert` : ""}`,
  });

  // 8 + 9 · Elektronik-BU / Maschinen-BU
  const sollTbuJa = isJa(v, "soll_tbu");
  const sollTbuOffen = !v["soll_tbu"];
  const sollTbuVs = num(v["soll_tbu.vs"]);
  const sollTbuHaft = num(v["soll_tbu.haftzeit"]);
  for (const [key, feld, label, hinweis] of [
    ["elektronik_bu", "elektronik_bu", "Elektronik-BU", "Abgrenzung zur Cyber-BU beachten."],
    [
      "maschinen_bu",
      "maschinen_bu",
      "Maschinen-BU",
      "Lange Lieferzeiten besonders berücksichtigen.",
    ],
  ] as const) {
    const istJa = isJa(v, feld);
    ergebnisse.push({
      key,
      pruefung: label,
      sollLabel: `Soll ${label}`,
      istLabel: label,
      logik: "Bedarf, VS/Limit, Haftzeit und SB vergleichen.",
      hinweis,
      status: deckungStatus(
        sollTbuJa,
        sollTbuOffen,
        istJa,
        sollTbuVs,
        istJa ? (num(v[`${feld}.vs`]) ?? num(v[`${feld}.limit_eur`])) : null,
        sollTbuHaft,
        istJa ? num(v[`${feld}.haftzeit`]) : null,
      ),
      detail: `Soll ${sollTbuJa ? "Ja" : sollTbuOffen ? "—" : "Nein"}${
        sollTbuVs !== null ? ` (${eur(sollTbuVs)})` : ""
      } · Vertrag ${istJa ? "Ja" : "Nein"}${
        istJa && v[`${feld}.vs`] ? ` (${eur(num(v[`${feld}.vs`]))})` : ""
      }`,
    });
  }

  // 10 · Betriebsschließung
  const istBsJa = isJa(v, "betriebsschliessung");
  ergebnisse.push({
    key: "betriebsschliessung",
    pruefung: "Betriebsschließung",
    sollLabel: "Soll Betriebsschließung",
    istLabel: "Betriebsschließung / Infektionsschutz",
    logik: "Bedarf, Limit, Dauer und Karenz vergleichen.",
    hinweis: "Deckungsauslöser und Ausschlüsse qualitativ prüfen.",
    status: deckungStatus(
      isJa(v, "soll_betriebsschliessung"),
      !v["soll_betriebsschliessung"],
      istBsJa,
      num(v["soll_betriebsschliessung.limit_eur"]),
      istBsJa ? num(v["betriebsschliessung.limit_eur"]) : null,
      null,
      null,
    ),
    detail: `Soll ${isJa(v, "soll_betriebsschliessung") ? "Ja" : v["soll_betriebsschliessung"] ? "Nein" : "—"}${
      v["soll_betriebsschliessung.dauer"] ? ` (${v["soll_betriebsschliessung.dauer"]})` : ""
    } · Vertrag ${istBsJa ? "Ja" : "Nein"}${
      istBsJa && v["betriebsschliessung.dauer"] ? ` (${v["betriebsschliessung.dauer"]})` : ""
    }`,
  });

  // 11 · Summenaktualität
  const bemessungsjahr = num(v["bemessungsjahr.jahr"]);
  const stichtag = v["bemessungsjahr.stichtag"] ? new Date(v["bemessungsjahr.stichtag"]) : null;
  const jahrDesStichtags =
    stichtag && !Number.isNaN(stichtag.getTime()) ? stichtag.getFullYear() : null;
  const basisJahr = bemessungsjahr ?? jahrDesStichtags;
  const alter = basisJahr === null ? null : heute.getFullYear() - basisJahr;
  ergebnisse.push({
    key: "summenaktualitaet",
    pruefung: "Summenaktualität",
    sollLabel: "Aktuelle Kennzahlen / Entwicklung",
    istLabel: "Bemessungsjahr / Stichtag",
    logik: "Veraltete Summenermittlung kennzeichnen und Neuprüfung auslösen.",
    hinweis: "Zeitgrenze sollte fachlich/organisatorisch konfigurierbar sein.",
    status:
      alter === null ? "offen" : alter >= SUMMENAKTUALITAET_JAHRE ? "pruefen" : "passend",
    detail:
      alter === null
        ? "Kein Bemessungsjahr erfasst."
        : `Bemessungsjahr ${basisJahr} · ${alter} Jahr${alter === 1 ? "" : "e"} alt (Grenze: ${SUMMENAKTUALITAET_JAHRE} Jahre)`,
  });

  return ergebnisse;
};

/**
 * Qualitative Hinweise aus Spalte E der Vertragsmaske — keine Rechenregeln,
 * aber Punkte, die im Abgleich sichtbar sein müssen.
 */
export type QualitativerHinweis = { key: string; titel: string; text: string };

export const qualitativeHinweise = (answers: EaAnswers): QualitativerHinweis[] => {
  const v = answers.values;
  const hinweise: QualitativerHinweis[] = [];

  const ohneKbu = gefahrenOhneKbu(answers.kbu);
  if (ohneKbu.length) {
    hinweise.push({
      key: "gefahren_ohne_kbu",
      titel: `${ohneKbu.length} Gefahr${ohneKbu.length === 1 ? "" : "en"} versichert, aber ohne KBU`,
      text: `${ohneKbu.map((g) => g.label).join(", ")} — Sachschaden ist gedeckt, der daraus folgende Ertragsausfall nicht.`,
    });
  }

  const unbewertet = EA_GEFAHREN.filter((g) => !answers.kbu[g.key]);
  if (unbewertet.length) {
    hinweise.push({
      key: "gefahren_offen",
      titel: `${unbewertet.length} Gefahr${unbewertet.length === 1 ? "" : "en"} ohne KBU-Status`,
      text: "Solange der Status offen ist, lässt sich nicht beurteilen, ob eine Abweichung vorliegt.",
    });
  }

  if (v["ausschluesse"]?.trim()) {
    hinweise.push({
      key: "ausschluesse",
      titel: "Ausschlüsse erfasst",
      text: "Qualitativ prüfen, ob ein Ausschluss den ermittelten Bedarf trifft.",
    });
  }

  if (v["sublimits"]?.trim()) {
    hinweise.push({
      key: "sublimits",
      titel: "Sublimits / besondere Voraussetzungen erfasst",
      text: "Verdeckte Limitierungen können die BU trotz ausreichender Versicherungssumme begrenzen.",
    });
  }

  if (v["dok_summenermittlung"] !== "ja") {
    hinweise.push({
      key: "nachweis",
      titel: "Kein Nachweis zur Summenermittlung",
      text: "Ohne Summenermittlung ist die Versicherungssumme im Schadenfall schwer zu verteidigen.",
    });
  }

  return hinweise;
};

/** Vorschlag für das Gesamtergebnis (Excel-Zeile 72) aus allen Einzelprüfungen. */
export const gesamtergebnisVorschlag = (
  ergebnisse: PruefErgebnis[],
): "passend" | "pruefen" | "luecke" => {
  if (ergebnisse.some((e) => e.status === "luecke")) return "luecke";
  if (ergebnisse.some((e) => e.status === "pruefen")) return "pruefen";
  return "passend";
};

export const STATUS_LABEL: Record<PruefStatus, string> = {
  passend: "passend",
  pruefen: "prüfen",
  luecke: "Deckungslücke",
  offen: "offen",
};
